import { useState, useEffect, useRef, useCallback } from "react";

const SERVER = "https://forex-backend-32m9.onrender.com";

const C = {
  bg:      "#060A0F",
  surface: "#0B1118",
  card:    "#0F1923",
  border:  "#1A2840",
  accent:  "#00C8FF",
  green:   "#00E676",
  red:     "#FF3347",
  gold:    "#FFD100",
  oil:     "#FF8C00",
  text:    "#E2F0FF",
  muted:   "#4A6A84",
  dim:     "#0D1825",
};

const ALL_PAIRS = [
  { sym:"XAU/USD", label:"XAU/USD", icon:"🥇", cat:"Gold",  pip:0.1,    dec:2 },
  { sym:"EUR/USD", label:"EUR/USD", icon:"💶", cat:"Forex", pip:0.0001, dec:5 },
  { sym:"GBP/USD", label:"GBP/USD", icon:"💷", cat:"Forex", pip:0.0001, dec:5 },
  { sym:"USD/JPY", label:"USD/JPY", icon:"💴", cat:"Forex", pip:0.01,   dec:3 },
  { sym:"WTI/USD", label:"WTI/USD", icon:"🛢️", cat:"Oil",   pip:0.01,   dec:2 },
  { sym:"XAG/USD", label:"XAG/USD", icon:"🥈", cat:"Gold",  pip:0.001,  dec:3 },
  { sym:"AUD/USD", label:"AUD/USD", icon:"🇦🇺", cat:"Forex", pip:0.0001, dec:5 },
  { sym:"USD/CHF", label:"USD/CHF", icon:"🇨🇭", cat:"Forex", pip:0.0001, dec:5 },
];

function Dot({color,size=8}){
  return(
    <span style={{position:"relative",display:"inline-flex",width:size,height:size,flexShrink:0}}>
      <span style={{position:"absolute",inset:0,borderRadius:"50%",background:color,opacity:.3,animation:"ping 1.6s ease-in-out infinite"}}/>
      <span style={{position:"absolute",inset:"18%",borderRadius:"50%",background:color}}/>
    </span>
  );
}

function Input({label,icon,type="text",value,onChange,placeholder}){
  return(
    <div style={{marginBottom:14}}>
      <div style={{fontSize:10,color:C.muted,marginBottom:5,letterSpacing:.7}}>{label}</div>
      <div style={{position:"relative"}}>
        <span style={{position:"absolute",left:12,top:"50%",transform:"translateY(-50%)",fontSize:16}}>{icon}</span>
        <input type={type} value={value} onChange={e=>onChange(e.target.value)} placeholder={placeholder}
          style={{width:"100%",background:C.dim,border:`1px solid ${C.border}`,borderRadius:11,padding:"12px 12px 12px 40px",color:C.text,fontSize:13,fontFamily:"'Sora',sans-serif",outline:"none"}}
          onFocus={e=>e.target.style.borderColor=C.accent}
          onBlur={e=>e.target.style.borderColor=C.border}/>
      </div>
    </div>
  );
}

function Ticker({prices,prev}){
  const ref=useRef(null);const pos=useRef(0);const raf=useRef(null);
  useEffect(()=>{
    const el=ref.current;if(!el)return;
    const go=()=>{pos.current-=0.5;if(Math.abs(pos.current)>=el.scrollWidth/2)pos.current=0;el.style.transform=`translateX(${pos.current}px)`;raf.current=requestAnimationFrame(go);};
    raf.current=requestAnimationFrame(go);return()=>cancelAnimationFrame(raf.current);
  },[]);
  const items=[...ALL_PAIRS,...ALL_PAIRS];
  return(
    <div style={{overflow:"hidden",background:C.surface,borderBottom:`1px solid ${C.border}`,padding:"7px 0"}}>
      <div ref={ref} style={{display:"flex",willChange:"transform",whiteSpace:"nowrap"}}>
        {items.map((t,i)=>{
          const p=prices[t.sym],pv=prev[t.sym],up=p&&pv?p>=pv:true;
          return(
            <span key={i} style={{display:"inline-flex",alignItems:"center",gap:6,padding:"0 16px",borderRight:`1px solid ${C.border}`}}>
              <span style={{fontFamily:"monospace",fontSize:10,color:C.muted}}>{t.label}</span>
              <span style={{fontFamily:"monospace",fontSize:12,fontWeight:700,color:C.text}}>{p?p.toFixed(t.dec):"—"}</span>
              <span style={{fontSize:10,color:up?C.green:C.red}}>{up?"▲":"▼"}</span>
            </span>
          );
        })}
      </div>
    </div>
  );
}

function SigCard({sig,onClick}){
  const col=sig.type==="BUY"?C.green:C.red;
  return(
    <div onClick={()=>onClick(sig)}
      style={{background:C.card,border:`1px solid ${col}44`,borderLeft:`3px solid ${col}`,borderRadius:14,padding:"14px 15px",marginBottom:11,cursor:"pointer",position:"relative",overflow:"hidden",transition:"transform .15s,box-shadow .15s"}}
      onMouseEnter={e=>{e.currentTarget.style.transform="translateY(-2px)";e.currentTarget.style.boxShadow=`0 8px 28px ${col}18`;}}
      onMouseLeave={e=>{e.currentTarget.style.transform="none";e.currentTarget.style.boxShadow="none";}}>
      <div style={{position:"absolute",top:0,right:0,bottom:0,width:"35%",background:`linear-gradient(to left,${col}09,transparent)`,pointerEvents:"none"}}/>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:12}}>
        <div style={{display:"flex",gap:9,alignItems:"center"}}>
          <span style={{fontSize:22}}>{ALL_PAIRS.find(p=>p.sym===sig.pair)?.icon||"💱"}</span>
          <div>
            <div style={{fontFamily:"monospace",fontWeight:700,fontSize:15,color:C.text,letterSpacing:1}}>{sig.pair}</div>
            <div style={{fontSize:10,color:C.muted,marginTop:2}}>{new Date(sig.openTime).toLocaleTimeString()}</div>
          </div>
        </div>
        <div style={{textAlign:"right"}}>
          <span style={{background:col+"22",color:col,borderRadius:6,padding:"3px 12px",fontFamily:"monospace",fontWeight:700,fontSize:12,letterSpacing:1}}>{sig.type}</span>
          <div style={{display:"flex",alignItems:"center",gap:4,justifyContent:"flex-end",marginTop:5}}>
            <Dot color={col} size={7}/>
            <span style={{fontSize:9,color:col}}>LIVE</span>
          </div>
        </div>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(5,1fr)",gap:5,marginBottom:10}}>
        {[["ENTRY",sig.entry,C.accent],["SL",sig.sl,C.red],["TP1",sig.tp1,C.green],["TP2",sig.tp2,C.green],["TP3",sig.tp3,C.green]].map(([l,v,c])=>(
          <div key={l} style={{background:C.dim,borderRadius:8,padding:"7px 3px",textAlign:"center"}}>
            <div style={{fontSize:8,color:C.muted,letterSpacing:.6,marginBottom:3}}>{l}</div>
            <div style={{fontFamily:"monospace",fontSize:9,color:c,fontWeight:700}}>{v}</div>
          </div>
        ))}
      </div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
        <div style={{display:"flex",alignItems:"center",gap:6}}>
          <div style={{height:3,background:C.dim,borderRadius:2,overflow:"hidden",width:80}}>
            <div style={{height:"100%",width:`${sig.confidence}%`,background:`linear-gradient(90deg,${col}66,${col})`,borderRadius:2}}/>
          </div>
          <span style={{fontFamily:"monospace",fontSize:9,color:C.muted}}>{sig.confidence}%</span>
        </div>
        <span style={{fontFamily:"monospace",fontSize:12,fontWeight:700,color:sig.pips>=0?C.green:C.red}}>
          {sig.pips>=0?"+":""}{sig.pips} pips
        </span>
      </div>
    </div>
  );
}

function HistoryCard({sig}){
  const col=sig.type==="BUY"?C.green:C.red;
  const won=sig.status==="tp1_hit"||sig.status==="tp1_hit_close"||sig.status==="tp2_hit"||sig.status==="tp3_hit"
  return(
    <div style={{background:C.card,border:`1px solid ${won?C.green+"33":C.red+"33"}`,borderLeft:`3px solid ${won?C.green:C.red}`,borderRadius:14,padding:"12px 15px",marginBottom:10}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
        <div style={{display:"flex",gap:8,alignItems:"center"}}>
          <span style={{fontSize:18}}>{ALL_PAIRS.find(p=>p.sym===sig.pair)?.icon||"💱"}</span>
          <div>
            <div style={{fontFamily:"monospace",fontWeight:700,fontSize:13,color:C.text}}>{sig.pair}</div>
            <div style={{fontSize:9,color:C.muted}}>{new Date(sig.openTime).toLocaleString()}</div>
          </div>
        </div>
        <div style={{textAlign:"right"}}>
          <span style={{background:col+"22",color:col,borderRadius:6,padding:"2px 8px",fontFamily:"monospace",fontWeight:700,fontSize:10}}>{sig.type}</span>
          <div style={{fontSize:10,color:won?C.green:C.red,marginTop:4,fontWeight:700}}>
            {sig.status==="tp3_hit"?"🎯 TP3":sig.status==="tp2_hit"?"✅ TP2":sig.status==="tp1_hit_close"||sig.status==="tp1_hit"?"TP1 HIT":"SL HIT"}
          </div>
        </div>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:6}}>
        <div style={{background:C.dim,borderRadius:8,padding:"6px",textAlign:"center"}}>
          <div style={{fontSize:8,color:C.muted,marginBottom:2}}>ENTRY</div>
          <div style={{fontFamily:"monospace",fontSize:10,color:C.accent,fontWeight:700}}>{sig.entry}</div>
        </div>
        <div style={{background:C.dim,borderRadius:8,padding:"6px",textAlign:"center"}}>
          <div style={{fontSize:8,color:C.muted,marginBottom:2}}>CLOSED</div>
          <div style={{fontFamily:"monospace",fontSize:10,color:C.text,fontWeight:700}}>{sig.closedAt||"—"}</div>
        </div>
        <div style={{background:C.dim,borderRadius:8,padding:"6px",textAlign:"center"}}>
          <div style={{fontSize:8,color:C.muted,marginBottom:2}}>PIPS</div>
          <div style={{fontFamily:"monospace",fontSize:12,color:sig.pips>=0?C.green:C.red,fontWeight:700}}>{sig.pips>=0?"+":""}{sig.pips}</div>
        </div>
      </div>
    </div>
  );
}

function Modal({item,onClose}){
  if(!item)return null;
  const col=item.type==="BUY"?C.green:C.red;
  return(
    <div onClick={onClose} style={{position:"fixed",inset:0,background:"rgba(0,0,0,.92)",zIndex:200,display:"flex",alignItems:"flex-end",backdropFilter:"blur(6px)"}}>
      <div onClick={e=>e.stopPropagation()} style={{background:C.surface,borderRadius:"22px 22px 0 0",border:`1px solid ${C.border}`,borderBottom:"none",padding:"22px 18px 40px",width:"100%",maxWidth:420,margin:"0 auto",animation:"up .28s ease",maxHeight:"90vh",overflowY:"auto"}}>
        <div style={{width:34,height:4,background:C.border,borderRadius:2,margin:"0 auto 18px"}}/>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
          <div style={{display:"flex",gap:10,alignItems:"center"}}>
            <span style={{fontSize:30}}>{ALL_PAIRS.find(p=>p.sym===item.pair)?.icon||"💱"}</span>
            <div>
              <div style={{fontFamily:"monospace",fontSize:20,fontWeight:700,color:C.text}}>{item.pair}</div>
              <div style={{fontSize:11,color:C.muted}}>Auto Signal · Engine v3.0</div>
            </div>
          </div>
          <span style={{background:col+"22",color:col,borderRadius:8,padding:"6px 16px",fontFamily:"monospace",fontWeight:700,fontSize:15}}>{item.type}</span>
        </div>
        <div style={{background:C.card,borderRadius:14,padding:"13px 16px",marginBottom:13,border:`1px solid ${col}33`,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <div>
            <div style={{fontSize:9,color:C.muted,marginBottom:3}}>ENTRY PRICE</div>
            <div style={{fontFamily:"monospace",fontSize:28,fontWeight:700,color:C.text}}>{item.entry}</div>
          </div>
          <div style={{textAlign:"right"}}>
            <div style={{fontSize:9,color:C.muted,marginBottom:3}}>CURRENT P&L</div>
            <div style={{fontFamily:"monospace",fontSize:18,fontWeight:700,color:item.pips>=0?C.green:C.red}}>{item.pips>=0?"+":""}{item.pips} pips</div>
          </div>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:9,marginBottom:13}}>
          {[["🛡️ Stop Loss",item.sl,C.red],["🎯 TP 1",item.tp1,C.green],["🎯 TP 2",item.tp2,C.green],["🎯 TP 3",item.tp3,C.green],[`📊 Confidence`,`${item.confidence}%`,C.gold],["📈 Status",item.status?.toUpperCase()||"RUNNING",C.accent]].map(([l,v,c])=>(
            <div key={l} style={{background:C.card,borderRadius:12,padding:"12px 13px",border:`1px solid ${c}22`}}>
              <div style={{fontSize:9,color:C.muted,marginBottom:4}}>{l}</div>
              <div style={{fontFamily:"monospace",fontSize:16,fontWeight:700,color:c}}>{v}</div>
            </div>
          ))}
        </div>
        {item.reasons&&item.reasons.length>0&&(
          <div style={{background:C.card,borderRadius:14,padding:14,border:`1px solid ${C.border}`,marginBottom:13}}>
            <div style={{fontSize:10,color:C.muted,marginBottom:10,letterSpacing:.7}}>✅ WHY THIS SIGNAL</div>
            {item.reasons.map((r,i)=>(<div key={i} style={{display:"flex",gap:8,marginBottom:7}}><span style={{color:col,fontSize:12}}>•</span><span style={{fontSize:12,color:C.text,lineHeight:1.5}}>{r}</span></div>))}
          </div>
        )}
        <div style={{background:"#FFD10011",borderRadius:12,padding:"10px 12px",border:`1px solid ${C.gold}33`,marginBottom:13}}>
          <div style={{fontSize:10,color:C.gold,lineHeight:1.6}}>⚠️ <strong>Risk Warning:</strong> Trading involves significant risk. Never risk more than 1-2% per trade.</div>
        </div>
      </div>
    </div>
  );
}

function AuthScreen({onLogin}){
  const [mode,setMode]=useState("login");
  const [email,setEmail]=useState("");
  const [password,setPassword]=useState("");
  const [name,setName]=useState("");
  const [loading,setLoading]=useState(false);
  const [msg,setMsg]=useState("");
  const handleSubmit=async()=>{
    if(!email||!password){setMsg("Please fill all fields");return;}
    setLoading(true);setMsg("");
    await new Promise(r=>setTimeout(r,1000));
    if(mode==="login"){onLogin({name:email.split("@")[0],email,avatar:email[0].toUpperCase()});}
    else if(mode==="register"){if(!name){setMsg("Please enter your name");setLoading(false);return;}onLogin({name,email,avatar:name[0].toUpperCase()});}
    else{setMsg("✅ Reset link sent!");setLoading(false);return;}
    setLoading(false);
  };
  return(
    <div style={{maxWidth:420,margin:"0 auto",minHeight:"100vh",background:C.bg,fontFamily:"'Sora',sans-serif",display:"flex",flexDirection:"column"}}>
      <div style={{background:`linear-gradient(160deg,${C.surface},${C.bg})`,padding:"50px 24px 32px",textAlign:"center",borderBottom:`1px solid ${C.border}`}}>
        <div style={{width:70,height:70,borderRadius:"50%",background:`linear-gradient(135deg,${C.accent},${C.green})`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:32,margin:"0 auto 16px"}}>⚡</div>
        <div style={{fontFamily:"monospace",fontSize:22,fontWeight:700,background:`linear-gradient(90deg,${C.accent},${C.green})`,WebkitBackgroundClip:"text",WebkitTextFillColor:"transparent"}}>FOREX SIGNAL</div>
        <div style={{fontSize:11,color:C.accent,marginTop:2,letterSpacing:1}}>BY FAISAL</div>
        <div style={{fontSize:12,color:C.muted,marginTop:8}}>Professional Auto Signals · Gold · Forex · Oil</div>
      </div>
      <div style={{flex:1,padding:"24px 20px 40px",overflowY:"auto"}}>
        <div style={{display:"flex",background:C.dim,borderRadius:12,padding:4,marginBottom:24}}>
          {[["login","Login"],["register","Sign Up"]].map(([m,l])=>(<button key={m} onClick={()=>{setMode(m);setMsg("");}} style={{flex:1,padding:"9px",borderRadius:9,border:"none",background:mode===m?C.card:"transparent",color:mode===m?C.text:C.muted,fontFamily:"'Sora',sans-serif",fontWeight:700,fontSize:13,cursor:"pointer"}}>{l}</button>))}
        </div>
        {mode==="register"&&<Input label="FULL NAME" icon="👤" value={name} onChange={setName} placeholder="Your Name"/>}
        <Input label="EMAIL" icon="📧" type="email" value={email} onChange={setEmail} placeholder="you@email.com"/>
        {mode!=="forgot"&&<Input label="PASSWORD" icon="🔒" type="password" value={password} onChange={setPassword} placeholder="••••••••"/>}
        {msg&&<div style={{background:msg.includes("✅")?C.green+"22":C.red+"22",border:`1px solid ${msg.includes("✅")?C.green:C.red}44`,borderRadius:10,padding:"10px 12px",marginBottom:14,fontSize:12,color:msg.includes("✅")?C.green:C.red}}>{msg}</div>}
        <button onClick={handleSubmit} disabled={loading} style={{width:"100%",padding:14,background:`linear-gradient(135deg,${C.accent},${C.green})`,border:"none",borderRadius:13,color:C.bg,fontFamily:"monospace",fontWeight:700,fontSize:14,cursor:"pointer",marginBottom:12,opacity:loading?0.7:1}}>
          {loading?"⟳ Please wait...":(mode==="login"?"🚀 LOGIN":"✅ CREATE ACCOUNT")}
        </button>
        {mode==="login"&&<div style={{textAlign:"center"}}><button onClick={()=>{setMode("forgot");setMsg("");}} style={{background:"none",border:"none",color:C.muted,fontSize:12,cursor:"pointer",textDecoration:"underline"}}>Forgot password?</button></div>}
        <div style={{marginTop:28,background:C.card,borderRadius:14,padding:14,border:`1px solid ${C.border}`}}>
          <div style={{display:"flex",justifyContent:"space-around",textAlign:"center"}}>
            {[["Auto","Signals"],["15","Strategies"],["24/7","Running"]].map(([v,l])=>(<div key={l}><div style={{fontFamily:"monospace",fontSize:16,fontWeight:700,color:C.accent}}>{v}</div><div style={{fontSize:9,color:C.muted,marginTop:2}}>{l}</div></div>))}
          </div>
        </div>
      </div>
    </div>
  );
}

function NotifCenter({signals,history,onClose}){
  const allNotifs=[
    ...signals.map(s=>({...s,msg:`Entry: ${s.entry} | TP1: ${s.tp1} | SL: ${s.sl}`,time:new Date(s.openTime).toLocaleTimeString(),read:false})),
    ...history.slice(0,5).map(s=>({...s,msg:`Closed ${s.pips>=0?"+":""}${s.pips} pips · ${s.status?.replace("_"," ").toUpperCase()}`,time:new Date(s.closedTime||s.openTime).toLocaleTimeString(),read:true}))
  ];
  return(
    <div onClick={onClose} style={{position:"fixed",inset:0,background:"rgba(0,0,0,.88)",zIndex:200,display:"flex",alignItems:"flex-start",backdropFilter:"blur(6px)",paddingTop:60}}>
      <div onClick={e=>e.stopPropagation()} style={{background:C.surface,borderRadius:"0 0 22px 22px",border:`1px solid ${C.border}`,borderTop:"none",padding:"0 0 20px",width:"100%",maxWidth:420,margin:"0 auto",maxHeight:"80vh",overflowY:"auto"}}>
        <div style={{padding:"16px 18px 12px",borderBottom:`1px solid ${C.border}`,display:"flex",justifyContent:"space-between",alignItems:"center",position:"sticky",top:0,background:C.surface}}>
          <div style={{fontWeight:700,fontSize:15,color:C.text}}>🔔 Notifications</div>
          <button onClick={onClose} style={{background:"none",border:"none",color:C.muted,cursor:"pointer",fontSize:18}}>✕</button>
        </div>
        {allNotifs.length===0&&<div style={{padding:20,textAlign:"center",color:C.muted,fontSize:12}}>No notifications yet</div>}
        {allNotifs.map((n,i)=>{
          const col=n.type==="BUY"?C.green:C.red;
          return(
            <div key={i} style={{padding:"13px 18px",borderBottom:`1px solid ${C.border}`,background:n.read?"transparent":col+"0A",display:"flex",gap:12,alignItems:"flex-start"}}>
              {!n.read&&<span style={{width:6,height:6,borderRadius:"50%",background:col,flexShrink:0,marginTop:6}}/>}
              {n.read&&<span style={{width:6,flexShrink:0}}/>}
              <span style={{fontSize:20,flexShrink:0}}>{ALL_PAIRS.find(p=>p.sym===n.pair)?.icon||"💱"}</span>
              <div style={{flex:1}}>
                <div style={{fontSize:13,fontWeight:700,color:C.text,marginBottom:3}}><span style={{color:col}}>{n.type}</span> · {n.pair}</div>
                <div style={{fontSize:11,color:C.muted}}>{n.msg}</div>
                <div style={{fontSize:10,color:C.muted,marginTop:4}}>{n.time}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ProfileScreen({user,onLogout}){
  return(
    <div style={{animation:"fade .3s ease"}}>
      <div style={{background:`linear-gradient(135deg,${C.accent}18,${C.green}0A)`,border:`1px solid ${C.accent}33`,borderRadius:18,padding:20,marginBottom:16,textAlign:"center"}}>
        <div style={{width:64,height:64,borderRadius:"50%",background:`linear-gradient(135deg,${C.accent},${C.green})`,display:"flex",alignItems:"center",justifyContent:"center",fontWeight:700,fontSize:26,color:C.bg,margin:"0 auto 12px"}}>{user.avatar}</div>
        <div style={{fontSize:18,fontWeight:700,color:C.text}}>{user.name}</div>
        <div style={{fontSize:12,color:C.muted,marginBottom:10}}>{user.email}</div>
        <span style={{background:C.green+"22",color:C.green,borderRadius:8,padding:"4px 14px",fontFamily:"monospace",fontWeight:700,fontSize:12}}>✅ ALL SIGNALS FREE</span>
      </div>
      <div style={{background:`linear-gradient(135deg,${C.green}18,${C.accent}0A)`,border:`1px solid ${C.green}44`,borderRadius:16,padding:14,marginBottom:14,textAlign:"center"}}>
        <div style={{fontSize:22,marginBottom:6}}>🎁</div>
        <div style={{fontSize:13,fontWeight:700,color:C.green,marginBottom:4}}>Everything is FREE!</div>
        <div style={{fontSize:11,color:C.muted}}>Gold · Forex · Oil · All signals unlocked. No payment needed.</div>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:14}}>
        {[["Auto","Signals"],["15","Strategies"],["6","API Keys"],["24/7","Running"]].map(([v,l])=>(
          <div key={l} style={{background:C.card,borderRadius:13,padding:"13px",border:`1px solid ${C.border}`,textAlign:"center"}}>
            <div style={{fontFamily:"monospace",fontSize:20,fontWeight:700,color:C.accent}}>{v}</div>
            <div style={{fontSize:10,color:C.muted,marginTop:3}}>{l}</div>
          </div>
        ))}
      </div>
      <button onClick={onLogout} style={{width:"100%",padding:13,background:"transparent",border:`1px solid ${C.red}44`,borderRadius:13,color:C.red,fontFamily:"monospace",fontWeight:700,fontSize:13,cursor:"pointer"}}>🚪 LOGOUT</button>
    </div>
  );
}

export default function App(){
  const [user,setUser]=useState(null);
  const [tab,setTab]=useState("signals");
  const [filter,setFilter]=useState("All");
  const [modal,setModal]=useState(null);
  const [showNotif,setShowNotif]=useState(false);
  const [prices,setPrices]=useState({});
  const [prev,setPrev]=useState({});
  const [activeSignals,setActiveSignals]=useState([]);
  const [history,setHistory]=useState([]);
  const [status,setStatus]=useState("CONNECTING");
  const [lastTime,setLastTime]=useState("");
  const [unread,setUnread]=useState(0);

  const fetchFromServer=useCallback(async()=>{
    try{
      const pr=await fetch(`${SERVER}/prices`);
      const pd=await pr.json();
      if(pd.prices&&Object.keys(pd.prices).length>0){
        setPrev(p=>({...p}));setPrices(pd.prices);
        setStatus("LIVE");setLastTime(new Date().toLocaleTimeString());
      }
      const sr=await fetch(`${SERVER}/signals`);
      const sd=await sr.json();
      const newActive=sd.active||[];
      setActiveSignals(newActive);
      setHistory(sd.history||[]);
      setUnread(newActive.length);
    }catch(e){setStatus("ERROR");}
  },[]);

  useEffect(()=>{
    if(!user)return;
    fetchFromServer();
    const iv=setInterval(fetchFromServer,10000);
    return()=>clearInterval(iv);
  },[user,fetchFromServer]);

  if(!user)return <AuthScreen onLogin={u=>setUser(u)}/>;

  const getPairCat=(sym)=>ALL_PAIRS.find(p=>p.sym===sym)?.cat||"Forex";
  const shown=filter==="All"?activeSignals:activeSignals.filter(s=>getPairCat(s.pair)===filter);
  const shownHistory=filter==="All"?history:history.filter(s=>getPairCat(s.pair)===filter);
  const winCount=history.filter(s=>s.status?.includes("tp")||s.status==="tp1_hit_close").length;
  const lossCount=history.filter(s=>s.status==="stopped").length;
  const totalPips=history.reduce((a,s)=>a+(s.pips||0),0);
  const winRate=history.length>0?Math.round((winCount/history.length)*100):0;

  return(
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;600;700&display=swap');
        @keyframes ping{0%{transform:scale(1);opacity:.3}70%,100%{transform:scale(2.4);opacity:0}}
        @keyframes up{from{transform:translateY(100%)}to{transform:translateY(0)}}
        @keyframes fade{from{opacity:0;transform:translateY(5px)}to{opacity:1;transform:translateY(0)}}
        *{box-sizing:border-box;margin:0;padding:0;-webkit-tap-highlight-color:transparent}
        input::placeholder{color:#4A6A84}::-webkit-scrollbar{width:0}
      `}</style>
      <div style={{maxWidth:420,margin:"0 auto",minHeight:"100vh",background:C.bg,fontFamily:"'Sora',sans-serif",color:C.text}}>

        {/* HEADER */}
        <div style={{background:C.surface,borderBottom:`1px solid ${C.border}`,padding:"13px 18px 10px",position:"sticky",top:0,zIndex:80}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <div>
              <div style={{fontFamily:"monospace",fontSize:17,fontWeight:700,background:`linear-gradient(90deg,${C.accent},${C.green})`,WebkitBackgroundClip:"text",WebkitTextFillColor:"transparent"}}>⚡ FOREX SIGNAL</div>
              <div style={{display:"flex",alignItems:"center",gap:5,marginTop:2}}>
                <Dot color={status==="LIVE"?C.green:C.gold} size={6}/>
                <span style={{fontSize:9,color:status==="LIVE"?C.green:C.gold,letterSpacing:.5}}>{status==="LIVE"?`AUTO · ${lastTime}`:"CONNECTING..."}</span>
              </div>
            </div>
            <div style={{display:"flex",alignItems:"center",gap:10}}>
              <button onClick={()=>{setShowNotif(true);setUnread(0);}} style={{position:"relative",background:"none",border:"none",cursor:"pointer",padding:4}}>
                <span style={{fontSize:22}}>🔔</span>
                {unread>0&&<span style={{position:"absolute",top:0,right:0,background:C.red,color:"#fff",borderRadius:"50%",width:16,height:16,fontSize:9,fontWeight:700,display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"monospace"}}>{unread}</span>}
              </button>
              <div onClick={()=>setTab("profile")} style={{width:34,height:34,borderRadius:"50%",background:`linear-gradient(135deg,${C.accent},${C.green})`,display:"flex",alignItems:"center",justifyContent:"center",fontWeight:700,fontSize:15,color:C.bg,cursor:"pointer"}}>{user.avatar}</div>
            </div>
          </div>
        </div>

        <Ticker prices={prices} prev={prev}/>

        <div style={{padding:"14px 13px 90px"}}>

          {tab==="signals"&&(
            <div style={{animation:"fade .3s ease"}}>
              <div style={{background:C.card,borderRadius:14,padding:"12px 14px",marginBottom:14,border:`1px solid ${C.border}`,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <div>
                  <div style={{fontSize:12,color:C.muted}}>Welcome back,</div>
                  <div style={{fontSize:15,fontWeight:700,color:C.text}}>{user.name} 👋</div>
                </div>
                <span style={{background:C.green+"22",color:C.green,borderRadius:8,padding:"4px 12px",fontFamily:"monospace",fontWeight:700,fontSize:11}}>✅ FREE FOR ALL</span>
              </div>

              <div style={{background:C.card,borderRadius:14,padding:"12px 14px",marginBottom:14,border:`1px solid ${C.green}33`}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                  <div>
                    <div style={{fontSize:10,color:C.muted,marginBottom:4}}>AUTO SIGNAL ENGINE v3.0 · 15 STRATEGIES</div>
                    <div style={{display:"flex",flexWrap:"wrap",gap:4,marginBottom:4}}>
                      {["RSI","EMA","MACD","BB","Stoch","ATR","WR","CCI","S&R","Candles","Structure","MTF"].map(s=>(<span key={s} style={{background:C.dim,borderRadius:4,padding:"2px 6px",fontSize:9,color:C.accent,fontWeight:700}}>{s}</span>))}
                    </div>
                    <div style={{fontSize:10,color:C.muted}}>{activeSignals.length} active · Auto updates every 5 min</div>
                  </div>
                  <button onClick={fetchFromServer} style={{background:C.dim,border:`1px solid ${C.border}`,borderRadius:10,padding:"6px 10px",color:C.muted,fontFamily:"monospace",fontSize:11,cursor:"pointer",flexShrink:0,marginLeft:8}}>↻</button>
                </div>
              </div>

              <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:6,marginBottom:14}}>
                {[[`${activeSignals.length}`,"Active"],[`${winCount}`,"Wins"],[`${lossCount}`,"Losses"],[`${totalPips>=0?"+":""}${totalPips}`,"Pips"]].map(([v,l])=>(
                  <div key={l} style={{background:C.card,borderRadius:12,padding:"10px 6px",border:`1px solid ${C.border}`,textAlign:"center"}}>
                    <div style={{fontFamily:"monospace",fontSize:16,fontWeight:700,color:l==="Wins"?C.green:l==="Losses"?C.red:l==="Pips"?totalPips>=0?C.green:C.red:C.accent}}>{v}</div>
                    <div style={{fontSize:9,color:C.muted,marginTop:2}}>{l}</div>
                  </div>
                ))}
              </div>

              <div style={{display:"flex",gap:7,marginBottom:13,overflowX:"auto",paddingBottom:2}}>
                {["All","Forex","Gold","Oil"].map(f=>(<button key={f} onClick={()=>setFilter(f)} style={{padding:"5px 14px",borderRadius:20,border:"none",background:filter===f?C.accent:C.dim,color:filter===f?C.bg:C.muted,fontFamily:"monospace",fontSize:11,fontWeight:700,cursor:"pointer",whiteSpace:"nowrap",flexShrink:0}}>{f}</button>))}
              </div>

              {shown.length>0&&(
                <>
                  <div style={{fontSize:10,color:C.green,marginBottom:10,letterSpacing:.7,display:"flex",alignItems:"center",gap:6}}><Dot color={C.green} size={6}/> LIVE SIGNALS</div>
                  {shown.map(s=><SigCard key={s.id} sig={s} onClick={setModal}/>)}
                </>
              )}

              {shown.length===0&&(
                <div style={{textAlign:"center",padding:"40px 20px",color:C.muted}}>
                  <div style={{fontSize:30,marginBottom:12}}>🔍</div>
                  <div style={{fontSize:13,marginBottom:6,color:C.text}}>No strong signals right now</div>
                  <div style={{fontSize:11,lineHeight:1.6}}>Engine scans every 5 minutes automatically. Signals appear when all 15 strategies agree strongly.</div>
                </div>
              )}

              {shownHistory.length>0&&(
                <>
                  <div style={{fontSize:10,color:C.muted,marginBottom:10,marginTop:20,letterSpacing:.7,display:"flex",alignItems:"center",gap:6}}>📋 SIGNAL HISTORY ({shownHistory.length})</div>
                  {shownHistory.map((s,i)=><HistoryCard key={i} sig={s}/>)}
                </>
              )}
            </div>
          )}

          {tab==="markets"&&(
            <div style={{animation:"fade .3s ease"}}>
              <div style={{background:C.card,borderRadius:16,padding:16,border:`1px solid ${C.border}`,marginBottom:14}}>
                <div style={{display:"flex",alignItems:"center",gap:7,marginBottom:13}}>
                  <Dot color={C.green} size={7}/>
                  <span style={{fontSize:11,color:C.green,fontWeight:700}}>LIVE PRICES · AUTO SERVER</span>
                  <button onClick={fetchFromServer} style={{marginLeft:"auto",background:C.dim,border:`1px solid ${C.border}`,borderRadius:6,padding:"3px 10px",color:C.muted,fontSize:9,cursor:"pointer",fontFamily:"monospace"}}>↻</button>
                </div>
                {ALL_PAIRS.map(t=>{const p=prices[t.sym],pv=prev[t.sym],up=p&&pv?p>=pv:true;return(
                  <div key={t.sym} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"9px 0",borderBottom:`1px solid ${C.border}`}}>
                    <div style={{display:"flex",alignItems:"center",gap:7}}><span style={{fontSize:16}}>{t.icon}</span><span style={{fontSize:12,color:C.text,fontWeight:600}}>{t.label}</span></div>
                    <div style={{display:"flex",alignItems:"center",gap:8}}><span style={{fontFamily:"monospace",fontSize:14,fontWeight:700,color:C.text}}>{p?p.toFixed(t.dec):"—"}</span><span style={{fontSize:11,color:up?C.green:C.red,fontWeight:700}}>{up?"▲":"▼"}</span></div>
                  </div>
                );})}
              </div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
                {[[`${winRate}%`,"Win Rate","All time"],[`${totalPips>=0?"+":""}${totalPips}`,"Total Pips","All signals"],[`${history.length}`,"Closed","Signals"],[`${activeSignals.length}`,"Active","Right now"]].map(([v,l,s])=>(
                  <div key={l} style={{background:C.card,borderRadius:14,padding:"14px 13px",border:`1px solid ${C.border}`}}>
                    <div style={{fontFamily:"monospace",fontSize:22,fontWeight:700,color:C.accent}}>{v}</div>
                    <div style={{fontSize:12,color:C.text,marginTop:4}}>{l}</div>
                    <div style={{fontSize:9,color:C.muted}}>{s}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {tab==="channel"&&(
            <div style={{animation:"fade .3s ease"}}>
              <div style={{background:`linear-gradient(135deg,${C.accent}18,${C.green}0A)`,border:`1px solid ${C.accent}44`,borderRadius:18,padding:20,marginBottom:13}}>
                <div style={{fontSize:9,color:C.accent,letterSpacing:.7,marginBottom:6}}>YOUR CHANNEL</div>
                <div style={{fontFamily:"monospace",fontSize:32,fontWeight:700,color:C.text}}>1,240</div>
                <div style={{fontSize:12,color:C.muted,marginBottom:12}}>Active Subscribers</div>
                <div style={{display:"flex",gap:22}}>
                  <div><div style={{fontFamily:"monospace",fontSize:18,color:C.green,fontWeight:700}}>$3,720</div><div style={{fontSize:9,color:C.muted}}>Monthly Revenue</div></div>
                  <div><div style={{fontFamily:"monospace",fontSize:18,color:C.gold,fontWeight:700}}>4.8 ⭐</div><div style={{fontSize:9,color:C.muted}}>Rating</div></div>
                </div>
              </div>
              {[["Free",680,"$0/mo",C.muted],["Pro",420,"$9/mo",C.accent],["VIP",140,"$29/mo",C.gold]].map(([p,u,pr,c])=>(
                <div key={p} style={{background:C.card,border:`1px solid ${c}33`,borderRadius:14,padding:"14px 16px",marginBottom:10,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                  <div><div style={{fontWeight:700,color:c,fontSize:13}}>{p} Plan</div><div style={{fontSize:11,color:C.muted,marginTop:2}}>{u} subscribers</div></div>
                  <div style={{fontFamily:"monospace",fontWeight:700,color:C.text,fontSize:14}}>{pr}</div>
                </div>
              ))}
              <button style={{width:"100%",padding:14,marginTop:6,background:`linear-gradient(135deg,${C.accent},${C.green})`,border:"none",borderRadius:14,color:C.bg,fontFamily:"monospace",fontWeight:700,fontSize:13,cursor:"pointer"}}>+ BROADCAST SIGNAL TO ALL</button>
            </div>
          )}

          {tab==="profile"&&<ProfileScreen user={user} onLogout={()=>setUser(null)}/>}
        </div>

        <div style={{position:"fixed",bottom:0,left:"50%",transform:"translateX(-50%)",width:"100%",maxWidth:420,background:C.surface,borderTop:`1px solid ${C.border}`,padding:"10px 0 22px",display:"flex",justifyContent:"space-around",zIndex:80}}>
          {[["signals","⚡","Signals"],["markets","📊","Markets"],["channel","👥","Channel"],["profile","👤","Profile"]].map(([id,ic,lb])=>(
            <button key={id} onClick={()=>setTab(id)} style={{background:"none",border:"none",cursor:"pointer",display:"flex",flexDirection:"column",alignItems:"center",gap:3,padding:"4px 14px"}}>
              <span style={{fontSize:20,filter:tab===id?"none":"grayscale(1) opacity(.35)"}}>{ic}</span>
              <span style={{fontSize:9,letterSpacing:.4,fontWeight:700,color:tab===id?C.accent:C.muted}}>{lb}</span>
            </button>
          ))}
        </div>

        {modal&&<Modal item={modal} onClose={()=>setModal(null)}/>}
        {showNotif&&<NotifCenter signals={activeSignals} history={history} onClose={()=>setShowNotif(false)}/>}
      </div>
    </>
  );
}
